"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import type { AreasComarcales, Tenant } from "@/types/types"
import type { User } from "@/types/models/user"
import {
  normalizeTenantId,
  getTenantEntityFromRaw,
  normalizeAreasComarcales,
  normalizeMunicipios,
  normalizeAmbitosIntervencion,
} from "@/lib/auth/auth.utils"
import { getAvailableTenantIds } from "@/lib/mappers/tenant.mappers"
import {
  fetchCurrentUserRepository,
  loginRepository,
  logoutRepository,
  updateCurrentUserRepository,
  setActiveTenantRepository,
} from "@/lib/repositories/user.repository"

export interface AuthContextType {
  isLoggedIn: boolean
  isLoading: boolean
  isAdmin: boolean
  isManager: boolean
  canEditFeder: boolean
  canManageFeder: boolean
  canEditDocuments: boolean
  canDeleteDocuments: boolean
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User | null) => void
  updateUser: (patch: Partial<User>) => Promise<void>
  refetchUser: () => Promise<void>
  currentTenantId: number | null
  currentTenant: Tenant | null
  availableTenantIds: number[]
  instanceName: string | null
  userLang: string
  areasComarcales: AreasComarcales[]
  municipios: any[]
  ambitosIntervencion: any[]
}

export function useAuthUser(): AuthContextType {
  const [user, setUser] = useState<User | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const clearAuth = useCallback(() => {
    setUser(null)
    setIsLoggedIn(false)
  }, [])

  const fetchMe = useCallback(async () => {
    const me = (await fetchCurrentUserRepository()) as User

    setUser(me)
    setIsLoggedIn(true)

    return me
  }, [])

  const refetchUser = useCallback(async () => {
    setIsLoading(true)

    try {
      await fetchMe()
    } catch {
      clearAuth()
    } finally {
      setIsLoading(false)
    }
  }, [fetchMe, clearAuth])

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true)

      try {
        await loginRepository(email, password)
        await fetchMe()
      } catch {
        clearAuth()
        toast.error("Error en el login", {
          description: "Por favor compruebe sus credenciales",
        })
        throw new Error("Login failed")
      } finally {
        setIsLoading(false)
      }
    },
    [fetchMe, clearAuth],
  )

  const logout = useCallback(async () => {
    setIsLoading(true)

    try {
      await logoutRepository()
    } finally {
      clearAuth()
      setIsLoading(false)
    }
  }, [clearAuth])

  const updateUser = useCallback(
    async (patch: Partial<User>) => {
      setIsLoading(true)

      try {
        await updateCurrentUserRepository(patch)
        await fetchMe()
      } finally {
        setIsLoading(false)
      }
    },
    [fetchMe],
  )

  useEffect(() => {
    void refetchUser()
  }, [refetchUser])

  useEffect(() => {
    void (async () => {
      if (!user || normalizeTenantId(user.active_tenant as Tenant)) return

      const firstTenant =
        user.tenants?.map(getTenantEntityFromRaw).find(Boolean) ?? null

      if (!firstTenant?.id) return

      setIsLoading(true)

      try {
        await setActiveTenantRepository(firstTenant.id)
        await fetchMe()
      } finally {
        setIsLoading(false)
      }
    })()
  }, [user, fetchMe])

  const currentTenant =
    (user?.active_tenant as Tenant | null | undefined) ?? null

  const currentTenantId = normalizeTenantId(currentTenant)

  const availableTenantIds = useMemo(
    () => getAvailableTenantIds(user?.tenants),
    [user?.tenants],
  )

  const isAdmin =
    (user as any)?.role?.name === "Admin" ||
    (user as any)?.role === "admin" ||
    (user as any)?.is_admin === true

  const isManager =
    isAdmin ||
    (user as any)?.role?.name === "Manager" ||
    (user as any)?.role === "manager" ||
    (user as any)?.is_manager === true

  const roleName = String((user as any)?.role?.name ?? (user as any)?.role ?? "")
    .trim()
    .toLowerCase()
  const isManagerPai =
    roleName === "manager pai" ||
    roleName === "manager_pai" ||
    roleName === "manager-pai"
  const canEditFeder = isAdmin || isManager || isManagerPai
  const canManageFeder = isAdmin || isManagerPai
  const canEditDocuments = isAdmin || isManager || isManagerPai
  const canDeleteDocuments = isAdmin || isManagerPai

  const areasComarcales = useMemo(
    () => normalizeAreasComarcales(currentTenant?.areas_comarcales),
    [currentTenant?.areas_comarcales],
  )

  const municipios = useMemo(
    () => normalizeMunicipios(currentTenant?.municipios),
    [currentTenant?.municipios],
  )

  const ambitosIntervencion = useMemo(
    () => normalizeAmbitosIntervencion(currentTenant?.ambitos_intervencion),
    [currentTenant?.ambitos_intervencion],
  )

  return useMemo(
    () => ({
      user,
      setUser,
      isLoggedIn,
      isLoading,
      login,
      logout,
      updateUser,
      refetchUser,
      isAdmin,
      isManager,
      canEditFeder,
      canManageFeder,
      canEditDocuments,
      canDeleteDocuments,
      currentTenant,
      currentTenantId,
      availableTenantIds,
      instanceName: currentTenant?.nombre ?? null,
      userLang: (user as any)?.language ?? (user as any)?.lang ?? "es",
      areasComarcales,
      municipios,
      ambitosIntervencion,
    }),
    [
      user,
      isLoggedIn,
      isLoading,
      login,
      logout,
      updateUser,
      refetchUser,
      isAdmin,
      isManager,
      canEditFeder,
      canManageFeder,
      canEditDocuments,
      canDeleteDocuments,
      currentTenant,
      currentTenantId,
      availableTenantIds,
      areasComarcales,
      municipios,
      ambitosIntervencion,
    ],
  )
}
